import { usePode } from '@/api/me';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import type { ItemResponse, SituacaoItem } from '@triade/contracts';
import { useItensPagina } from '@/api/itens';
import { BarraDeAcoesLista, TAMANHOS_PAGINA } from '@/components/barra-lista';
import { SeletorCategoria } from '@/components/categorias/seletor-categoria';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { useDebounced } from '@/hooks/use-debounced';
import { formatarContagem } from '@/lib/format';
import { BotaoRelatorios } from '@/relatorios';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { ItensTabela } from './itens-tabela';
import { CLASSIFICACOES, paraConsulta, paraFiltro, ROTULO_CLASSIFICACAO, ROTULO_SITUACAO, ROTULO_TIPO, SITUACOES, TAMANHO_PAGINA, temFiltro, TIPOS, type BuscaItens, type TipoItem } from './tipos';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none';

/** Tela "Produtos e serviços — lista" (/cadastros/itens). Busca, filtros, paginacao e total vem da API (GET /itens/pagina). */
export function ItensListaPage({ busca }: { busca: BuscaItens }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const podeEditar = usePode().pode('item.editar');
  const pagina = busca.pagina ?? 1;
  const tamanho = busca.tamanho ?? TAMANHO_PAGINA;
  const lista = useItensPagina(paraConsulta(busca));

  const mudar = useCallback(
    (mudanca: Partial<BuscaItens>) => {
      void navigate({ to: '/cadastros/itens', search: { ...busca, ...mudanca }, replace: true });
    },
    [navigate, busca],
  );
  const abrir = useCallback(
    (i: ItemResponse) => void navigate({ to: '/cadastros/itens/$id', params: { id: i.id }, search: paraFiltro(busca) }),
    [navigate, busca],
  );

  const [termo, setTermo] = useState(busca.q ?? '');
  const termoAtraso = useDebounced(termo.trim(), 300);
  useEffect(() => {
    if (termoAtraso !== (busca.q ?? '')) mudar({ q: termoAtraso || undefined, pagina: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termoAtraso]);
  useEffect(() => setTermo(busca.q ?? ''), [busca.q]);

  const total = lista.data?.total ?? 0;
  const itens = lista.data?.itens ?? [];

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os itens.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroFormacao erro={lista.error} onTentar={() => void lista.refetch()} /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando itens…</Mensagem>;
  else if (itens.length === 0) {
    corpo = temFiltro(busca) ? (
      <Mensagem papel="status">
        Nenhum item com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/cadastros/itens', search: {}, replace: true })}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum item cadastrado.
        {podeEditar && (
          <>
            {' '}
            <Link to="/cadastros/itens/novo" className="font-medium text-primary hover:underline">
              Cadastre o primeiro
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else corpo = <ItensTabela itens={itens} busca={busca} onAbrir={abrir} />;

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Produtos e serviços
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Produtos e serviços</h1>
        </div>
      </div>

      <section aria-label="Lista de itens" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
          <div role="tablist" aria-label="Tipo" className="flex flex-wrap gap-1 rounded-[10px] bg-line-soft p-[3px]">
            {([undefined, ...TIPOS] as Array<TipoItem | undefined>).map((t) => {
              const ativa = busca.tipo === t;
              return (
                <button
                  key={t ?? 'todos'}
                  type="button"
                  role="tab"
                  aria-selected={ativa}
                  onClick={() => mudar({ tipo: t, pagina: undefined })}
                  className={cn('h-[30px] rounded-lg px-3 text-small', ativa ? 'bg-card font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.08)]' : 'text-ink-secondary hover:text-ink')}
                >
                  {t ? ROTULO_TIPO[t] : 'Todos'}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex h-[34px] w-[240px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
              <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
              <label htmlFor="itens-q" className="sr-only">
                Buscar item
              </label>
              <input
                id="itens-q"
                type="search"
                value={termo}
                onChange={(e) => setTermo(e.target.value)}
                placeholder="Código ou descrição"
                autoComplete="off"
                className="w-full min-w-0 bg-transparent text-small outline-none"
              />
            </div>
            <label htmlFor="itens-custo" className="sr-only">
              Custo
            </label>
            <select
              id="itens-custo"
              value={busca.semCusto === undefined ? '' : String(busca.semCusto)}
              onChange={(e) => mudar({ semCusto: e.target.value === '' ? undefined : e.target.value === 'true', pagina: undefined })}
              className={CAMPO}
            >
              <option value="">Todos os custos</option>
              <option value="true">Sem custo</option>
              <option value="false">Com custo</option>
            </select>
            <label htmlFor="itens-classificacao" className="sr-only">
              Classificação
            </label>
            <select
              id="itens-classificacao"
              value={busca.classificacao ?? ''}
              onChange={(e) => mudar({ classificacao: (e.target.value || undefined) as BuscaItens['classificacao'], pagina: undefined })}
              className={CAMPO}
            >
              <option value="">Todas as classificações</option>
              {CLASSIFICACOES.map((c) => (
                <option key={c} value={c}>
                  {ROTULO_CLASSIFICACAO[c]}
                </option>
              ))}
            </select>
            <SeletorCategoria
              id="itens-categoria"
              rotulo="Categoria"
              ocultarRotulo
              comTodas
              value={busca.categoriaId}
              onChange={(v) => mudar({ categoriaId: v, pagina: undefined })}
              className="h-[34px] w-auto rounded-[9px]"
            />
            <label htmlFor="itens-situacao" className="sr-only">
              Situação
            </label>
            <select
              id="itens-situacao"
              value={busca.situacao ?? ''}
              onChange={(e) => mudar({ situacao: (e.target.value || undefined) as SituacaoItem | undefined, pagina: undefined })}
              className={CAMPO}
            >
              <option value="">Todas as situações</option>
              {SITUACOES.map((s) => (
                <option key={s} value={s}>
                  {ROTULO_SITUACAO[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        {corpo}
      </section>
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Novo item', onClick: () => void navigate({ to: '/cadastros/itens/novo', search: paraFiltro(busca) }) } : undefined}
        paginacao={{
          pagina,
          tamanho,
          total,
          onPagina: (p) => mudar({ pagina: p > 1 ? p : undefined }),
          onTamanho: (t) => mudar({ tamanho: t === TAMANHO_PAGINA ? undefined : t, pagina: undefined }),
          tamanhos: [...TAMANHOS_PAGINA],
        }}
        relatorios={<BotaoRelatorios tela="itens" params={{ q: busca.q, tipo: busca.tipo, situacao: busca.situacao, semCusto: busca.semCusto === undefined ? undefined : String(busca.semCusto), classificacao: busca.classificacao, categoriaId: busca.categoriaId }} />}
      />
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
